import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'
import type { App } from 'vue'

import type { CurrentUser } from '@/javascript/interfaces'

const bugsnagApiKey = import.meta.env.BUGSNAG_JS_API_KEY

// Hosts of third-party scripts loaded from external CDNs. Errors whose stack
// originates in one of these scripts are vendor-internal (e.g. the Appcues
// iframe DOM patcher reading contentDocument.body on a null contentDocument)
// and are not actionable in our codebase. Add a host here when a new hosted
// vendor script generates non-actionable noise.
export const THIRD_PARTY_SCRIPT_HOSTS = [
	'fast.appcues.com',
	'px.ads.linkedin.com',
	'snap.licdn.com',
	'www.google-analytics.com',
	'www.googletagmanager.com',
]

/**
 * Determines whether an error's stack trace originates in a known third-party
 * vendor script, in which case it is not actionable by us and should not be
 * reported to Bugsnag.
 * @param stacktrace - Stack frames from the reported error
 * @returns true when any frame belongs to a denylisted vendor host
 */
export const isThirdPartyScriptError = (
	stacktrace: Array<{ file?: string }>
): boolean =>
	stacktrace.some(frame =>
		THIRD_PARTY_SCRIPT_HOSTS.some(host => frame.file?.includes(host))
	)

const getCurrentUser = (): Partial<CurrentUser> | undefined => {
	if (typeof window === 'undefined') {
		return undefined
	}

	return window.current_user
}

if (bugsnagApiKey && typeof window !== 'undefined') {
	const currentUser = getCurrentUser()

	Bugsnag.start({
		apiKey: bugsnagApiKey,
		appVersion: import.meta.env.APP_VERSION || 'unknown',
		onError(event) {
			const errorMessage = event.errors[0]?.errorMessage ?? ''
			const stacktrace = event.errors[0]?.stacktrace ?? []

			// "InboundFilters" is a Zendesk-specific identifier that does not exist anywhere
			// in our codebase, so this message is always a Zendesk widget error.
			// Remove this filter once the Zendesk widget is removed.
			if (errorMessage.includes('InboundFilters')) {
				return false
			}

			// Stack frames with file === 'unknown' indicate scripts that are not served by our
			// infrastructure and cannot be traced back to our code (e.g. browser extensions,
			// injected third-party scripts). Suppress these as they are not actionable.
			if (stacktrace.some(frame => frame.file === 'unknown')) {
				return false
			}

			// Errors originating from Chrome extension scripts are not part of our codebase
			// and are not actionable.
			if (stacktrace.some(frame => frame.file?.startsWith('chrome-extension://'))) {
				return false
			}

			// Errors originating from hosted third-party vendor scripts (e.g. Appcues,
			// analytics/ads pixels) are vendor-internal and not actionable by us.
			if (isThirdPartyScriptError(stacktrace)) {
				return false
			}

			return true
		},
		plugins: [new BugsnagPluginVue()],
		user: {
			email: currentUser?.email,
			id: currentUser?.id?.toString(),
			name: currentUser?.name,
		},
	})
}

/**
 * Attaches Bugsnag to a Vue 3 application
 * @param app - Vue 3 application instance
 */
export default function (app: App) {
	if (!bugsnagApiKey || typeof window === 'undefined') {
		if (import.meta.env.DEV) {
			return
		}

		return
	}

	try {
		const bugsnagVue = Bugsnag.getPlugin('vue')

		if (bugsnagVue && app) {
			app.use(bugsnagVue)
		}
	} catch (error) {
		if (import.meta.env.DEV) {
			console.error('Failed to initialize Bugsnag Vue plugin:', error)
		}
	}
}
